const DESKTOP_QUERY = "(min-width: 992px)";

/**
 * Handles the responsive team cards and department tabs.
 *
 * @package AGDS
 * @category Modules
 * @module Team
 * @see FrontPage
 * @author Andy Groove
 * @since 1.0.0
 */
export default class Team {
    /**
     * @param {string|HTMLElement} sectionSelector
     */
    constructor(sectionSelector) {
        this.section = typeof sectionSelector === "string" ? document.querySelector(sectionSelector) : sectionSelector;
        if (!this.section) return;

        this.tabList = this.section?.querySelector('[role="tablist"]');
        this.tabs = this.tabList ? Array.from(this.tabList.querySelectorAll('[role="tab"]')) : [];
        this.rows = new Map(Array.from(this.section?.querySelectorAll(".team-row") || []).map((row) => [row.id, row]));
        this.cards = Array.from(this.section?.querySelectorAll(".team-card") || []);
        this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
        this.activeTab = null;
        this.openFrame = null;
        this.defaultCards = new Map();

        this.init();
    }

    /**
     * Initializes team card and tab interactions.
     *
     * @returns {void}
     */
    init() {
        if (!this.section || !this.tabList || !this.tabs.length || !this.rows.size) {
            return;
        }

        this.section.addEventListener("click", this.handleClick);
        this.section.addEventListener("keydown", this.handleKeydown);
        this.section.addEventListener("pointerover", this.handlePointerOver);
        this.section.addEventListener("pointerout", this.handlePointerOut);
        this.section.addEventListener("focusin", this.handleFocusIn);
        this.section.addEventListener("focusout", this.handleFocusOut);
        this.desktopQuery.addEventListener("change", this.handleViewportChange);

        const selectedTab = this.tabs.find((tab) => tab.getAttribute("aria-selected") === "true");
        this.setActiveTab(selectedTab || this.tabs[0]);
    }

    /**
     * Selects the requested department tab and exposes its row.
     *
     * @param {HTMLButtonElement} tab
     * @param {boolean} force
     * @returns {void}
     */
    setActiveTab(tab, force = false) {
        if (!tab || (tab === this.activeTab && !force)) {
            return;
        }

        this.activeTab = tab;

        this.tabs.forEach((item) => {
            const selected = item === tab;
            item.setAttribute("aria-selected", String(selected));
            item.tabIndex = selected ? 0 : -1;
        });

        this.rows.forEach((row, id) => {
            row.classList.toggle("_open", id === tab.dataset.rowId);
        });

        this.cards.forEach((card) => this.setCardOpen(card, false));
        this.cancelPendingCardOpen();

        if (!this.desktopQuery.matches) {
            this.openFrame = requestAnimationFrame(() => {
                this.openFrame = null;

                if (this.desktopQuery.matches || this.activeTab !== tab) {
                    return;
                }

                const row = this.rows.get(tab.dataset.rowId);
                const firstCard = row?.querySelector(".team-card");

                if (firstCard) {
                    this.setCardOpen(firstCard, true);
                }
            });
        } else {
            this.openRandomCardsPerRow();
        }
    }

    /**
     * Opens a random card in every row, using a distinct card index per row.
     *
     * @returns {void}
     */
    openRandomCardsPerRow() {
        const rows = Array.from(this.rows.values())
            .map((row) => ({
                row,
                cards: Array.from(row.querySelectorAll(".team-card")),
            }))
            .filter(({ cards }) => cards.length > 0)
            .sort((first, second) => first.cards.length - second.cards.length);
        const usedIndices = new Set();

        rows.forEach(({ row, cards }) => {
            const availableIndices = Array.from({ length: cards.length }, (_, index) => index).filter(
                (index) => !usedIndices.has(index),
            );

            if (availableIndices.length === 0) {
                const defaultCard = cards[Math.floor(Math.random() * cards.length)];
                this.defaultCards.set(row, defaultCard);
                this.setCardOpen(defaultCard, true);
                return;
            }

            const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
            usedIndices.add(randomIndex);
            const defaultCard = cards[randomIndex];
            this.defaultCards.set(row, defaultCard);
            this.setCardOpen(defaultCard, true);
        });
    }

    /**
     * Restores the default desktop card for the row containing the given card.
     *
     * @param {HTMLElement} card
     * @returns {void}
     */
    restoreDefaultCard(card) {
        const row = card.closest(".team-row");
        const defaultCard = row ? this.defaultCards.get(row) : null;

        if (defaultCard) {
            this.setCardOpen(defaultCard, true);
        }
    }

    /**
     * Cancels a queued card expansion after a subsequent interaction.
     *
     * @returns {void}
     */
    cancelPendingCardOpen() {
        if (this.openFrame !== null) {
            cancelAnimationFrame(this.openFrame);
            this.openFrame = null;
        }
    }

    /**
     * Opens one card and closes its siblings.
     *
     * @param {HTMLElement} card
     * @param {boolean} open
     * @returns {void}
     */
    setCardOpen(card, open) {
        if (open) {
            card.parentElement?.querySelectorAll(".team-card").forEach((sibling) => {
                if (sibling !== card) {
                    this.setCardOpen(sibling, false);
                }
            });
            card.classList.add("_open");
            card.querySelector(".team-card-toggle")?.setAttribute("aria-expanded", "true");
            return;
        }

        card.classList.remove("_open");
        card.querySelector(".team-card-toggle")?.setAttribute("aria-expanded", "false");
    }

    /**
     * Handles clicks on tabs and mobile cards.
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    handleClick = (event) => {
        const tab = event.target.closest('[role="tab"]');

        if (tab && this.tabList.contains(tab)) {
            this.setActiveTab(tab);
            return;
        }

        const card = event.target.closest(".team-card");

        if (card && this.section.contains(card) && !this.desktopQuery.matches) {
            this.cancelPendingCardOpen();
            this.setCardOpen(card, true);
        }
    };

    /**
     * Handles keyboard navigation for tabs and mobile cards.
     *
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    handleKeydown = (event) => {
        const tab = event.target.closest('[role="tab"]');

        if (tab && this.tabList.contains(tab)) {
            const currentIndex = this.tabs.indexOf(tab);
            let nextIndex;

            switch (event.key) {
                case "ArrowRight":
                    nextIndex = (currentIndex + 1) % this.tabs.length;
                    break;
                case "ArrowLeft":
                    nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
                    break;
                case "Home":
                    nextIndex = 0;
                    break;
                case "End":
                    nextIndex = this.tabs.length - 1;
                    break;
                default:
                    return;
            }

            event.preventDefault();
            this.tabs[nextIndex].focus();
            this.setActiveTab(this.tabs[nextIndex]);
            return;
        }
    };

    /**
     * Opens a card when a mouse or pen pointer enters it on desktop.
     *
     * @param {PointerEvent} event
     * @returns {void}
     */
    handlePointerOver = (event) => {
        const card = event.target.closest(".team-card");

        if (
            card &&
            this.section.contains(card) &&
            this.desktopQuery.matches &&
            event.pointerType !== "touch" &&
            !card.contains(event.relatedTarget)
        ) {
            this.setCardOpen(card, true);
        }
    };

    /**
     * Restores the row's default card after the pointer leaves.
     *
     * @param {PointerEvent} event
     * @returns {void}
     */
    handlePointerOut = (event) => {
        const card = event.target.closest(".team-card");
        const relatedCard = event.relatedTarget instanceof Element ? event.relatedTarget.closest(".team-card") : null;

        if (
            card &&
            this.section.contains(card) &&
            this.desktopQuery.matches &&
            event.pointerType !== "touch" &&
            !card.contains(event.relatedTarget) &&
            !card.matches(":focus-within") &&
            relatedCard?.parentElement !== card.parentElement
        ) {
            this.restoreDefaultCard(card);
        }
    };

    /**
     * Opens a card when it receives keyboard focus on desktop.
     *
     * @param {FocusEvent} event
     * @returns {void}
     */
    handleFocusIn = (event) => {
        const card = event.target.closest(".team-card");

        if (card && this.section.contains(card) && this.desktopQuery.matches) {
            this.setCardOpen(card, true);
        }
    };

    /**
     * Restores the row's default card after focus leaves unless the pointer is over it.
     *
     * @param {FocusEvent} event
     * @returns {void}
     */
    handleFocusOut = (event) => {
        const card = event.target.closest(".team-card");
        const relatedCard = event.relatedTarget instanceof Element ? event.relatedTarget.closest(".team-card") : null;

        if (
            card &&
            this.section.contains(card) &&
            this.desktopQuery.matches &&
            !card.contains(event.relatedTarget) &&
            !card.matches(":hover") &&
            relatedCard?.parentElement !== card.parentElement
        ) {
            this.restoreDefaultCard(card);
        }
    };

    /**
     * Synchronizes expanded cards when the viewport crosses the desktop breakpoint.
     *
     * @returns {void}
     */
    handleViewportChange = () => {
        this.setActiveTab(this.activeTab, true);
    };

    /**
     * Automatically initializes the animation if the section exists on the page.
     * @param {string} [selector='.team-section'] - Section selector.
     * @returns {Team|null} Instance or null if element is missing.
     */
    static init(selector = ".team-section") {
        const element = document.querySelector(selector);
        return element ? new Team(element) : null;
    }
}
